import { useMemo, useState } from 'react';
import { PageShell } from '../components/PageShell';
import { FiltrosFinanceiro } from '../components/financeiro/FiltrosFinanceiro';
import { FormReceita } from '../components/financeiro/FormReceita';
import { GraficoAging } from '../components/financeiro/GraficoAging';
import { GraficoRecebimentoMensal } from '../components/financeiro/GraficoRecebimentoMensal';
import { GraficoSocios } from '../components/financeiro/GraficoSocios';
import { GraficoTipoReceita } from '../components/financeiro/GraficoTipoReceita';
import { GraficoTopClientes } from '../components/financeiro/GraficoTopClientes';
import { KpiGrid } from '../components/financeiro/KpiGrid';
import { TabelaReceitas } from '../components/financeiro/TabelaReceitas';
import {
  useConfigFinanceiro, useDashboardFinanceiro, useExcluirReceita, useMarcarReceitaRecebida,
  useMe, useReceitas, useSocios,
} from '../hooks';
import type { FinReceitaFiltros, Receita, StatusReceita } from '../types';

type Aba = 'dashboard' | 'receitas';
const LS_ABA = 'financeiro.aba';

export function Financeiro() {
  const [aba, setAba] = useState<Aba>(() => (localStorage.getItem(LS_ABA) as Aba) || 'dashboard');
  const [filtros, setFiltros] = useState<FinReceitaFiltros>({});
  const [formAberto, setFormAberto] = useState(false);
  const [editando, setEditando] = useState<Receita | null>(null);

  const { data: dashboard, isLoading: loadingDash } = useDashboardFinanceiro();
  const { data: receitas = [], isFetching: loadingReceitas } = useReceitas(filtros);
  const { data: socios = [] } = useSocios();
  const { data: config } = useConfigFinanceiro();
  const { data: me } = useMe();
  const receber = useMarcarReceitaRecebida();
  const excluir = useExcluirReceita();

  function trocarAba(a: Aba) { setAba(a); localStorage.setItem(LS_ABA, a); }

  const minhaCota = useMemo(() => {
    if (!dashboard || !me) return null;
    const meuSocio = socios.find((s) => s.usuario_id === me.id);
    if (!meuSocio) return null;
    const linha = dashboard.por_socio.find((p) => p.socio_id === meuSocio.id);
    return linha ? { nome: meuSocio.nome, valor: linha.total } : null;
  }, [dashboard, me, socios]);

  function novaReceita() { setEditando(null); setFormAberto(true); }
  function editar(r: Receita) { setEditando(r); setFormAberto(true); }
  function verStatus(s: StatusReceita) { setFiltros({ status: s }); trocarAba('receitas'); }
  function verVencidos() { setFiltros({}); trocarAba('receitas'); }
  function aoReceber(r: Receita) { receber.mutate({ id: r.id }); }
  function aoExcluir(r: Receita) {
    if (window.confirm(`Excluir a receita do processo ${r.numero_processo}? Esta ação não pode ser desfeita.`)) {
      excluir.mutate(r.id);
    }
  }

  const tabCls = (a: Aba) =>
    `px-3 py-1 text-sm rounded-lg transition ${aba === a
      ? 'bg-blue-600 text-white'
      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`;

  return (
    <PageShell
      subbarra={
        <>
          <div className="flex items-center gap-3 min-w-0">
            <span className="font-semibold text-gray-800 dark:text-gray-100 whitespace-nowrap">💰 Financeiro</span>
            <div className="flex items-center gap-1">
              <button className={tabCls('dashboard')} onClick={() => trocarAba('dashboard')}>Dashboard</button>
              <button className={tabCls('receitas')} onClick={() => trocarAba('receitas')}>Receitas</button>
            </div>
          </div>
          <button onClick={novaReceita}
            className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 whitespace-nowrap">
            + Nova receita
          </button>
        </>
      }
    >
      {dashboard?.aviso_pct_socios && (
        <div className="mb-4 text-xs px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-900">
          ⚠ Os percentuais padrão dos sócios não somam 100% (contratuais e/ou sucumbência). Ajuste em
          <span className="font-medium"> Administração → Sócios &amp; honorários</span>.
        </div>
      )}

      {aba === 'dashboard' && (
        loadingDash || !dashboard ? (
          <div className="text-center text-sm text-slate-400 py-16">Carregando dashboard…</div>
        ) : (
          <div className="space-y-4">
            <KpiGrid d={dashboard} onVerStatus={verStatus} onVerVencidos={verVencidos} minhaCota={minhaCota} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <GraficoSocios dados={dashboard.por_socio} />
              <GraficoTipoReceita dados={dashboard.contratuais_sucumbencia} />
              <GraficoRecebimentoMensal dados={dashboard.por_mes} />
              <GraficoAging dados={dashboard.aging} />
              <div className="lg:col-span-2">
                <GraficoTopClientes dados={dashboard.por_cliente} />
              </div>
            </div>
          </div>
        )
      )}

      {aba === 'receitas' && (
        <div className="space-y-3">
          <FiltrosFinanceiro filtros={filtros} onChange={setFiltros} />
          <TabelaReceitas receitas={receitas} carregando={loadingReceitas && receitas.length === 0}
            onEditar={editar} onReceber={aoReceber} onExcluir={aoExcluir} />
        </div>
      )}

      {formAberto && (
        <FormReceita
          receita={editando}
          socios={socios}
          pctPadrao={config?.pct_contratuais_padrao ?? 0}
          onClose={() => { setFormAberto(false); setEditando(null); }}
        />
      )}
    </PageShell>
  );
}
